/**
 * Runtime validator for the {@link Layout} shape.
 *
 * Hand-rolled because the shape is small and we want zero new dependencies.
 * Returns either a typed Layout or a list of error paths — never throws on
 * untrusted input. The Rails-side validator (Phase D-6) mirrors these rules
 * exactly; if you change one, change the other.
 */

import { SHELL_NAMES, type Layout, type PrimitiveInstance, type ShellName } from './types'

export type ValidationError = { path: string; message: string }

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; errors: ValidationError[] }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isShellName(value: unknown): value is ShellName {
  return typeof value === 'string' && (SHELL_NAMES as readonly string[]).includes(value)
}

function validatePrimitiveInstance(
  value: unknown,
  path: string,
  errors: ValidationError[],
): PrimitiveInstance | null {
  if (!isPlainObject(value)) {
    errors.push({ path, message: 'primitive instance must be an object' })
    return null
  }

  const id = value.id
  const primitive = value.primitive
  const config = value.config

  let valid = true
  if (typeof id !== 'string' || id.length === 0) {
    errors.push({ path: `${path}.id`, message: 'id must be a non-empty string' })
    valid = false
  }
  if (typeof primitive !== 'string' || primitive.length === 0) {
    errors.push({ path: `${path}.primitive`, message: 'primitive must be a non-empty string' })
    valid = false
  }
  if (config !== undefined && !isPlainObject(config)) {
    errors.push({ path: `${path}.config`, message: 'config must be an object when present' })
    valid = false
  }

  if (!valid) return null
  return {
    id: id as string,
    primitive: primitive as string,
    config: (config as Record<string, unknown>) ?? {},
  }
}

export function validateLayout(input: unknown): ValidationResult<Layout> {
  const errors: ValidationError[] = []

  if (!isPlainObject(input)) {
    return { ok: false, errors: [{ path: '$', message: 'layout must be an object' }] }
  }

  // version
  if (input.version !== 1) {
    errors.push({ path: '$.version', message: 'version must be the literal 1' })
  }

  // shell
  if (!isShellName(input.shell)) {
    errors.push({
      path: '$.shell',
      message: `shell must be one of ${SHELL_NAMES.join(', ')}`,
    })
  }

  // slots
  if (!isPlainObject(input.slots)) {
    errors.push({ path: '$.slots', message: 'slots must be an object' })
    return { ok: false, errors }
  }

  const validatedSlots: Record<string, PrimitiveInstance[]> = {}
  for (const [slotName, slotValue] of Object.entries(input.slots)) {
    if (!Array.isArray(slotValue)) {
      errors.push({
        path: `$.slots.${slotName}`,
        message: 'slot value must be an array of primitive instances',
      })
      continue
    }
    const validInstances: PrimitiveInstance[] = []
    slotValue.forEach((item, index) => {
      const validated = validatePrimitiveInstance(
        item,
        `$.slots.${slotName}[${index}]`,
        errors,
      )
      if (validated) validInstances.push(validated)
    })
    validatedSlots[slotName] = validInstances
  }

  if (errors.length > 0) {
    return { ok: false, errors }
  }

  return {
    ok: true,
    value: {
      version: 1,
      shell: input.shell as ShellName,
      slots: validatedSlots,
    },
  }
}

/**
 * Convenience for places that want to throw on invalid input (server boundary,
 * default-layout fixtures). For untrusted input, prefer {@link validateLayout}.
 */
export function assertLayout(input: unknown): Layout {
  const result = validateLayout(input)
  if (!result.ok) {
    const summary = result.errors
      .map((e) => `${e.path}: ${e.message}`)
      .join('; ')
    throw new Error(`Invalid layout: ${summary}`)
  }
  return result.value
}
